import React from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../request'
import { Button } from './simple/button'
import {
  IconBackwards,
  IconCaretLeft,
  IconCaretRight,
  IconForward,
  IconSort,
  IconSortDown,
  IconSortUp,
} from '../icons/library'
import { exportCSV } from '../utils'

export type VisibleColumnDef<T> =
  | (ColumnDef<T> & { show?: undefined; exportOnly?: boolean })
  | (ColumnDef<T> & { show: boolean; id: string; exportOnly?: boolean })

type TableProps<T> = {
  /** Additional classes for styling the table. */
  className?: string
  /** Number of rows to display per page. Defaults to 10. Set to 0 to disable pagination */
  pagination?: number
  /** Search term for filtering the table. */
  search?: string
  /** Whether to enable sorting on the table. */
  sortable?: boolean
  /** The name for the export file. If provided, an export button will be shown. */
  exportName?: string
  /** Function to get sub-rows for nested tables. See tanstack table docs for more info. */
  getSubRows?: (row: T) => T[] | undefined
  /** What rows are called in 'No rows found' message. */
  rowName?: string
  /** Default sort order for the table. */
  defaultSort?: SortingState
}

type LocalTableProps<T> = {
  /** The column definitions for the table. This receives VisibleColumnDef to allow the `show: boolean` property in column definitions. */
  columns: VisibleColumnDef<T>[]
  /** The data to display in the table. */
  data: T[]
  /** Whether to keep the current page when the data changes. */
  saveLocationOnDataChange?: boolean
} & TableProps<T>

type RemoteTableResponse<T> = {
  /** The columns returned from the server, if any. */
  columns?: VisibleColumnDef<T>[]
  /** The data for the loaded page. */
  data: T[]
  /** The total number of rows in the table. */
  total: number
  /** The total number of rows matching the current filter. */
  totalFiltered: number
}

type RemoteTableProps<T> = {
  /** Where to send the request to, and what data to send alongside the search and page etc.. */
  remote: {
    url: string
    data: any
  }
  /** The column definitions for the table. This receives VisibleColumnDef to allow the `show: boolean` property in column definitions. */
  columns?: VisibleColumnDef<T>[]
} & TableProps<T>

/**
 * A table that loads data from a remote source. This is a wrapper around the tanstack table library.
 */
export const RemoteTable = <T,>({
  remote,
  className,
  pagination = 10,
  search: rawSearch,
  sortable,
  columns,
  exportName,
  getSubRows,
  rowName,
  defaultSort = [],
}: RemoteTableProps<T>) => {
  const [sorting, setSorting] = React.useState<SortingState>(defaultSort)
  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: pagination,
  })

  const [search, setSearch] = React.useState(rawSearch)
  React.useEffect(() => {
    setSearch(rawSearch)
    setPagination({ pageIndex: 0, pageSize })
  }, [rawSearch])

  const requestData = React.useMemo(
    () => ({
      ...remote.data,
      pageIndex,
      pageSize,
      sort: sortable ? sorting[0] : undefined,
      search,
    }),
    [remote.data, sorting, pageIndex, search]
  )
  const { data, loading } = useRequest<RemoteTableResponse<T>>(remote.url, requestData)
  const [exporting, setExporting] = React.useState(false)

  const processedColumns = React.useMemo(() => {
    const c = columns || data?.columns || []
    if (!sortable) return c
    return c.map((column) => {
      // @ts-ignore typescript is confused here
      if (column.cell && !column.accessor && !column.accessorFn && !column.accessorKey)
        return { ...column, accessorKey: '_' } // needed for sorting
      return column
    })
  }, [columns, data?.columns])

  const table = useReactTable({
    data: data?.data || [],
    columns: processedColumns,
    defaultColumn: {
      minSize: 0,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    paginateExpandedRows: false,
    filterFromLeafRows: true,
    getSubRows: getSubRows,
    onSortingChange: setSorting,
    enableSorting: sortable,
    enableMultiSort: false,
    manualSorting: sortable,
    enableGlobalFilter: search !== undefined,
    state: {
      sorting,
      globalFilter: search,
      ...(pagination !== undefined && {
        pagination: { pageIndex, pageSize },
      }),
      columnVisibility: processedColumns.reduce((acc, col) => ({ ...acc, [col.id || '']: col.show }), {}),
    },
    ...(pagination !== undefined && {
      onPaginationChange: setPagination,
      pageCount: Math.ceil((data?.totalFiltered || 0) / pageSize),
      manualPagination: true,
    }),
  })

  const handleExport = () => {
    setExporting(true)
    makeRequest<RemoteTableResponse<T>>(remote.url, { ...requestData, export: true })
      .then((response) => doExport(processedColumns, response.data, exportName || 'export'))
      .catch(showAxiosErrorAsToast)
      .finally(() => setExporting(false))
  }

  return renderTable(
    table,
    `${className} ${loading ? 'opacity-50' : ''}`,
    exportName === undefined ? undefined : handleExport,
    exporting,
    pagination,
    pageIndex,
    rowName
  )
}

/**
 * forwardRef is not typed correctly for components that use generics
 */
function hackyForwardRefTypeFixForTable<T, P = {}>(
  render: (props: P, ref: React.Ref<T>) => React.ReactNode
): (props: P & React.RefAttributes<T>) => React.ReactNode {
  return React.forwardRef(render as any) as any
}

export type LocalTableRef<T> = {
  filteredRows: T[]
}

/**
 * A table that loads data from a local source. This is a wrapper around the tanstack table library.
 * Exposes the filtered rows to the parent component through a ref.
 */
export const LocalTable = hackyForwardRefTypeFixForTable(
  <T,>(
    {
      columns,
      data,
      className,
      pagination = 10,
      search,
      sortable,
      exportName,
      saveLocationOnDataChange,
      getSubRows,
      rowName,
      defaultSort = [],
    }: LocalTableProps<T>,
    ref: React.Ref<LocalTableRef<T>>
  ) => {
    const [sorting, setSorting] = React.useState<SortingState>(defaultSort)
    const [{ pageIndex: rawPageIndex, pageSize }, setPagination] = React.useState({
      pageIndex: 0,
      pageSize: pagination,
    })

    const pageIndexOverrideRef = React.useRef<number | undefined>(undefined)
    React.useEffect(() => {
      // override the pagination if the data changes (table automatically resets to page 0, this prevents that)
      if (saveLocationOnDataChange) {
        const prevPageIndex = Math.min(rawPageIndex, Math.max(0, Math.ceil(data.length / pageSize) - 1))
        pageIndexOverrideRef.current = prevPageIndex
        const t = setTimeout(() => {
          setPagination({ pageIndex: prevPageIndex, pageSize })
          pageIndexOverrideRef.current = undefined
        }, 5)
        return () => clearTimeout(t)
      }
    }, [data])
    const pageIndex = pageIndexOverrideRef.current ?? rawPageIndex

    const table = useReactTable({
      data,
      columns,
      defaultColumn: {
        minSize: 0,
        size: Number.MAX_SAFE_INTEGER,
        maxSize: Number.MAX_SAFE_INTEGER,
      },
      getCoreRowModel: getCoreRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      paginateExpandedRows: false,
      filterFromLeafRows: true,
      getSubRows: getSubRows,
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      onSortingChange: setSorting,
      enableSorting: sortable,
      enableMultiSort: false,
      enableGlobalFilter: search !== undefined,
      state: {
        sorting,
        globalFilter: search,
        ...(pagination && {
          pagination: { pageIndex, pageSize },
        }),
        columnVisibility: columns.reduce((acc, col) => ({ ...acc, [col.id || '']: col.show !== false }), {}),
      },
      ...(pagination && {
        onPaginationChange: setPagination,
        getPaginationRowModel: getPaginationRowModel(),
      }),
    })

    React.useImperativeHandle(ref, () => ({
      get filteredRows() {
        return table.getFilteredRowModel().rows.map((r) => r.original)
      },
    }))

    return renderTable(
      table,
      `${className}`,
      exportName === undefined ? undefined : () => doExport(columns, data, exportName || 'export'),
      false,
      pagination,
      pageIndex,
      rowName
    )
  }
)

const renderTable = <T,>(
  table: Table<T>,
  className: string,
  exportAction: (() => void) | undefined,
  exporting: boolean,
  pagination: number | undefined,
  pageIndex: number,
  rowName: string = 'row'
) => (
  <div className={`${className} flex-col overflow-x-auto`}>
    <table className="w-full">
      <thead className="text-nowrap">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className="">
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                onClick={header.column.getToggleSortingHandler()}
                className={`border-b border-b-slate-200 border-l-slate-100 px-2 py-1 [&:not(:first-child)]:border-l ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                style={getSizeStyle(header.column.columnDef)}
              >
                {header.isPlaceholder ? null : (
                  <div className="flex items-center justify-between gap-1">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {(header.column.getCanSort() &&
                      {
                        asc: <IconSortUp opacity={0.75} size="sm" />,
                        desc: <IconSortDown opacity={0.75} size="sm" />,
                        false: <IconSort opacity={0.2} size="sm" />,
                      }[header.column.getIsSorted() as string]) ??
                      null}
                  </div>
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.length === 0 ? (
          <tr className="odd:bg-slate-50 hover:bg-slate-100">
            <td colSpan={table.getAllColumns().length} className="px-4 py-2 text-center font-bold">
              No {rowName}s found
            </td>
          </tr>
        ) : (
          table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="odd:bg-slate-50 hover:bg-slate-100">
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={`break-words border-slate-500/10 px-3 py-1 [&:not(:first-child)]:border-l ${row.getCanExpand() ? 'cursor-pointer' : ''} ${row.getIsExpanded() || !!row.getParentRow() ? 'border-l-4 border-l-slate-200' : ''}`}
                  onClick={row.getToggleExpandedHandler()}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
    {(exportAction || !!pagination) && (
      <div className="mt-2 flex justify-between gap-2">
        {exportAction ? (
          <Button onClick={exportAction} small disabled={exporting} showLoader={exporting}>
            Export As CSV
          </Button>
        ) : (
          <div />
        )}
        {!!pagination && table.getPageCount() > 1 && <Pagination table={table} pageIndex={pageIndex} />}
      </div>
    )}
  </div>
)

const getSizeStyle = (def: ColumnDef<any>) => ({
  width: def.size && def.size < 9999 ? `${def.size}px` : undefined,
  maxWidth: def.maxSize && def.maxSize < 9999 ? `${def.maxSize}px` : undefined,
  minWidth: def.minSize && def.minSize > 0 ? `${def.minSize}px` : undefined,
})

const Pagination = <T,>({ table, pageIndex }: { table: Table<T>; pageIndex: number }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm">
      Page {pageIndex + 1} of {table.getPageCount()}
    </span>
    <Button small onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
      <IconBackwards />
    </Button>
    <Button small onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
      <IconCaretLeft />
    </Button>
    <Button small onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
      <IconCaretRight />
    </Button>
    <Button small onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
      <IconForward />
    </Button>
  </div>
)

const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj)
}

const doExport = <T,>(columns: VisibleColumnDef<T>[], data: T[], exportName: string) => {
  const exportableColumns = columns.filter(
    (c) => (typeof c.header === 'string' || typeof c.id === 'string') && (c.show !== false || c.exportOnly === true)
  )
  const headers = exportableColumns.map((c) => (typeof c.header === 'string' ? c.header : c.id) as string)
  const rows = data.map((row) => exportableColumns.map((col) => getExportableValue(col, row)))
  exportCSV(headers, rows, exportName)
}

const getExportableValue = <T,>(col: VisibleColumnDef<T>, row: T) => {
  const tcol = col as { accessorKey?: string; accessorFn?: (t: T) => any }
  let raw = tcol.accessorKey
    ? getNestedValue(row, tcol.accessorKey)
    : tcol.accessorFn
      ? tcol.accessorFn(row)
      : undefined
  if (!['string', 'number', 'boolean'].includes(typeof raw)) return undefined
  return raw as string | number | boolean
}
